import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { ProduitsService } from 'app/services/produits/produits.service';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface Product {
  id: number;
  name: string;
  createdAt: string;
  createdAtDate?: Date;
  displayOrder?: number;
  orderIndex?: number;
  imageUrl?: string;
  sous_cat?: any[];
  [key: string]: any;
}

@Component({
  selector: 'app-produits-list',
  templateUrl: './produits-list.component.html',
  styleUrls: ['./produits-list.component.scss']
})
export class ProduitsListComponent implements OnInit {
  idAgency: any;
  Agency: any;
  list: Product[] = [];
  isLoading = false;
  loadingSousCat = false;

  constructor(
    private agencieService: AgenciesService,
    private activatedRoute: ActivatedRoute,
    private produitsService: ProduitsService,
    private sousCategorieService: SousCategorieService,
    private awsService: AwscontrolService,  // <-- injected
    private location: Location,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.idAgency = this.activatedRoute.snapshot.paramMap.get('id');
    this.agencieService.setIdAgency(this.idAgency);
    this.loadProduits();
  }

  private parseDate(dateString: string): Date {
    if (!dateString) return new Date(0);
    try {
      const [datePart, timePart] = dateString.split(' ');
      const [day, month, year] = datePart.split('/').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second);
    } catch {
      return new Date(0);
    }
  }

  loadProduits(): void {
    this.isLoading = true;
    this.agencieService.getAgencieById(this.idAgency).subscribe(
      (data: any) => {
        this.Agency = data;
        let produits = this.Agency?.produits?.filter((p: any) => p && p.id) || [];
        produits = produits.map((p: any) => ({
          ...p,
          createdAtDate: this.parseDate(p.createdAt),
          sous_cat: []
        }));
        this.list = produits;
        this.initializeOrderIndexes();
        this.loadSousCategories();
      },
      (error) => {
        console.error('Error fetching products:', error);
        this.isLoading = false;
        Swal.fire('Erreur', 'Impossible de charger les produits.', 'error');
      }
    );
  }

  loadSousCategories(): void {
    this.loadingSousCat = true;
    this.sousCategorieService.getListSousCategorie().subscribe({
      next: (allSousCats: any[]) => {
        const sousCatByProduct = new Map<number, any[]>();
        allSousCats.forEach(sc => {
          const productId = sc.produit_id || sc.produitId;
          if (productId) {
            if (!sousCatByProduct.has(productId)) {
              sousCatByProduct.set(productId, []);
            }
            sousCatByProduct.get(productId)!.push(sc);
          }
        });
        this.list.forEach(product => {
          product.sous_cat = sousCatByProduct.get(product.id) || [];
        });
        this.loadingSousCat = false;
      },
      error: (err) => {
        console.error('Failed to load sous-categories:', err);
        this.loadingSousCat = false;
      }
    });
  }

  initializeOrderIndexes(): void {
    const hasOrder = this.list.every(p => p.displayOrder !== undefined && p.displayOrder !== null);
    if (hasOrder) {
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      this.list.sort((a, b) => {
        const dateA = a.createdAtDate?.getTime() || 0;
        const dateB = b.createdAtDate?.getTime() || 0;
        return dateB - dateA;
      });
    }
    this.list.forEach((product, index) => {
      product.orderIndex = index + 1;
      product.displayOrder = index;
    });
  }

  // Batch save all products order
  private saveAllOrdersBatch(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    
    const updates = this.list.map(product => ({
      id: product.id,
      displayOrder: product.displayOrder!
    }));
    
    this.produitsService.updateProduitOrdersBatch(updates).subscribe({
      next: (response) => {
        this.isLoading = false;
        Swal.fire({
          title: 'Succès!',
          text: 'L\'ordre a été sauvegardé.',
          icon: 'success',
          timer: 1000,
          showConfirmButton: false
        });
      },
      error: (err) => {
        console.error('Batch update error:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de sauvegarder l\'ordre.',
          icon: 'error'
        });
      }
    });
  }

  // Called after any reordering (drag & drop, move up/down)
  private updateOrdersAndSave(): void {
    this.list.forEach((product, idx) => {
      product.orderIndex = idx + 1;
      product.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  drop(event: CdkDragDrop<Product[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  sortByDate(): void {
    this.list.sort((a, b) => {
      const dateA = a.createdAtDate?.getTime() || 0;
      const dateB = b.createdAtDate?.getTime() || 0;
      return dateB - dateA;
    });
    this.list.forEach((product, idx) => {
      product.orderIndex = idx + 1;
      product.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  sortByName(): void {
    this.list.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    this.list.forEach((product, idx) => {
      product.orderIndex = idx + 1;
      product.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  resetOrder(): void {
    this.list.sort((a, b) => {
      const dateA = a.createdAtDate?.getTime() || 0;
      const dateB = b.createdAtDate?.getTime() || 0;
      return dateB - dateA;
    });
    this.list.forEach((product, idx) => {
      product.orderIndex = idx + 1;
      product.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  // ========== UPDATED: DELETE PRODUCT WITH IMAGE DELETION ==========
  deleteProduct(product: Product): void {
    Swal.fire({
      title: "Supprimer ce produit ?",
      text: `Êtes-vous sûr de vouloir supprimer "${product.name}" ? L'image associée sera également supprimée.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler"
    }).then((result) => {
      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        // Step 1: Delete image from AWS (if exists)
        const deleteImage$ = product.imageUrl 
          ? this.awsService.deleteFile(product.imageUrl)
          : Promise.resolve({ status: 'success' } as any); // no-op

        // Using toPromise() for async/await style (or subscribe)
        // We'll use a promise chain to handle sequential deletion
        // Convert observable to promise
        const imageDeletionPromise = product.imageUrl 
          ? this.awsService.deleteFile(product.imageUrl).toPromise()
          : Promise.resolve({ status: 'success' });

        imageDeletionPromise
          .then((awsResponse: any) => {
            // Image deletion successful (or no image)
            if (awsResponse && awsResponse.status === 'success') {
              console.log('Image deleted from AWS');
            } else if (awsResponse && awsResponse.error) {
              console.warn('AWS deletion warning:', awsResponse.error);
              // Still proceed with product deletion
            }
            // Step 2: Delete product from backend
            return this.produitsService.deleteProduit(product.id).toPromise();
          })
          .then(() => {
            Swal.close();
            Swal.fire('Supprimé!', 'Le produit et son image ont été supprimés.', 'success');
            // Remove from list and re-index
            this.list = this.list.filter(p => p.id !== product.id);
            this.list.forEach((p, idx) => {
              p.orderIndex = idx + 1;
              p.displayOrder = idx;
            });
            this.saveAllOrdersBatch();
          })
          .catch((error) => {
            Swal.close();
            console.error('Delete error:', error);
            // If error is from AWS deletion, we still try to delete product
            // But here we treat any error as failure
            Swal.fire('Erreur', 'Impossible de supprimer le produit ou son image.', 'error');
          });
      }
    });
  }

  // For backward compatibility (used in template)
  deleteAgenciesProjet(produit: any): void {
    this.deleteProduct(produit);
  }

  navigateBack(): void {
    this.location.back();
  }
}